import {
  Component,
  Mixins,
  Prop,
  Ref,
  Vue,
  Watch,
} from 'vue-property-decorator'
import cytoscape from '@/app/cytoscape'
import DiagramService from '@/app/services/diagram-data-service'
import saveAs from 'file-saver'
import style from '@/diagrams/configs/message-spider-style'
import bus from '@/app/bus'
import Node from '@/diagrams/models/node'
import CreateNodeEvent from '@/diagrams/events/create-node-event'
import cytoService from '@/diagrams/services/cyto-service'
import Project from '@/projects/entities/project'
import ProjectsService from '@/projects/services/projects-service'
import { ProjectStatus } from '@/projects/entities/project-status'
import EditNodeEvent from '@/diagrams/events/edit-node-event'
import { EventObject } from 'cytoscape'
import { DiagramView } from '@/diagrams/models/diagram-views'
import Visual from '@/diagrams/models/visual'
import SpiderData from '@/diagrams/models/spider-data'

@Component
export default class MSpider extends Vue implements Visual {
  private PADDING = 50

  private project: Project | null = null
  private url = ''
  private tag = ''
  private selected: Node | null = null
  private isMultiDiagram = false
  private view: 'both' | 'first' | 'second' = 'both'
  private data: SpiderData | null = null

  get archived() {
    return (
      this.project !== null && this.project.status === ProjectStatus.Archived
    )
  }

  get isProactive() {
    return this.tag == 'proactive'
  }

  get messageType() {
    return this.isProactive ? 'proaktiv' : 'reaktiv'
  }

  get showFirst() {
    return this.view === 'both' || this.view === 'first'
  }

  get showSecond() {
    return this.view === 'both' || this.view === 'second'
  }

  public async setActionAsync(ctx: DiagramView) {
    this.url = ctx.url
    this.tag = ctx.tag
    await this.reloadAsync()
  }

  // === EXPORT ===

  public async saveAsync(format: string) {
    this.saveInstance(window.cyMessage1, format, 0)
    if (this.isMultiDiagram) {
      this.saveInstance(window.cyMessage2, format, 1)
    }
  }

  public saveInstance(instance: cytoscape.Core, format: string, num: number) {
    if (instance) {
      switch (format) {
        case 'svg':
          this.saveSVG(instance, num)
          break
        default:
          this.savePNG(instance, num)
          break
      }
    }
  }

  private saveSVG(instance: cytoscape.Core, num: number) {
    // @ts-ignore
    const contents = instance.svg({ scale: 1, full: true })
    const blob = new Blob([contents], { type: 'image/svg+xmlcharset=utf-8' })
    saveAs(blob, `botschaften-spinne-${this.messageType}.svg`)
  }

  private savePNG(instance: cytoscape.Core, num: number) {
    const contents = instance.png({ full: true, bg: '#ffffff' })
    saveAs(contents, `botschaften-spinne-${this.messageType}.png`)
  }

  // === SETUP ===

  private async reloadAsync() {
    await this.fetchDataAsync()
    if (document.readyState != 'complete') {
      document.addEventListener('DOMContentLoaded', () => {
        this.draw(this.data)
      })
    } else {
      this.draw(this.data)
    }
  }

  private async fetchDataAsync() {
    this.project = await ProjectsService.findAsync(this.$route.params.projectId)

    this.data = await DiagramService.spider(
      this.$route.params.projectId,
      'projectTopicGroupsTopics'
    )
  }

  private showView(view: 'both' | 'first' | 'second') {
    this.view = view
    if (this.data) {
      this.drawInstances(this.data)
      // FIXME: Damit der linke Graph korrekt gezeichnet wird.
      if (view === 'both') {
        this.drawInstances(this.data)
      }
    }
  }

  private draw(data: SpiderData | null) {
    if (data) {
      // Determine if the diagram is split up into two.
      this.isMultiDiagram = data.elements2.length > 1
      this.view = this.isMultiDiagram ? 'both' : 'first'
      this.drawInstances(data)
    }
  }

  private drawInstances(data: SpiderData) {
    this.$nextTick(() => {
      window.cyMessage1 = this.drawInstance('cym', data.elements1)
      if (data.elements2.length > 1) {
        window.cyMessage2 = this.drawInstance('czm', data.elements2)
      } else {
        window.cyMessage2 = this.drawInstance('czm', [])
      }
    })
  }

  private drawInstance(id: string, elements: any[]) {
    // if ((<any>window).id) {
    //   return (<any>window).id
    // }

    // if (window.cyTopic1) window.cyTopic1.destroy()
    // if (window.cyTopic2) window.cyTopic2.destroy()
    // if (window.cyMessage1) window.cyMessage1.destroy()
    // if (window.cyMessage2) window.cyMessage2.destroy()

    this.prepareMessages(elements)

    // !!! Lokale cy Instanz funktioniert nicht - Endlosschleife.
    // @ts-ignore
    const instance = cytoscape({
      container: document.getElementById(id),
      // @ts-ignore
      style,
      elements,
      wheelSensitivity: 0.2,
    })

    instance.ready(() => {
      instance.on('tap', 'node', (evt: EventObject) =>
        this.toggleNode(instance, evt)
      )

      instance
        .style()
        // @ts-ignore
        .selector(':selected')
      // .css({
      //   "overlay-color": "black",
      //   "overlay-padding": '5',
      //   "overlay-opacity": '0.25',
      // })

      this.layoutGraph(instance)
    })

    return instance
  }

  private layoutGraph(instance: cytoscape.Core) {
    const layout = instance.layout({
      name: 'concentric',
      startAngle: (5 / 4) * Math.PI,
      clockwise: true,
      animate: false,
      concentric: (node: any) => {
        switch (node.data('type')) {
          case 'root':
            return 10
          case 'node':
            return 5
          default:
            return 1
        }
      },
      stop: () => {
        const layout2 = instance.layout({
          name: 'cose-bilkent',
          // @ts-ignore
          animate: false,
          randomize: false,
          padding: 40,
          nodeDimensionsIncludeLabels: true,
          stop: () => {
            instance.fit(undefined, this.PADDING)
          },
        })
        layout2.run()
      },
    })
    layout.run()
  }

  private prepareMessages(elements: any[]) {
    for (let i = 0; i < elements.length; i++) {
      if (
        elements[i]['data'].type === 'leaf' ||
        elements[i]['data'].type === 'outer' ||
        elements[i]['data'].type === 'group'
      ) {
        elements.splice(i, 1)
        i--
      }
    }

    for (let i = 0; i < elements.length; i++) {
      if (elements[i]['data'].type === 'node') {
        elements.push({
          group: null,
          classes: null,
          pannable: false,
          data: {
            id: 'message-node-' + i,
            type: 'leaf',
            name: 'leafnode',
            message: this.isProactive
              ? elements[i]['data'].proactiveMessage
              : elements[i]['data'].reactiveMessage,
            source: null,
            target: null,
            parent: null,
            bias: elements[i]['data'].bias,
          },
        }),
          elements.push({
            group: null,
            classes: null,
            pannable: false,
            data: {
              id: 'message-node-' + (i + 1),
              type: 'outer',
              name: 'leafnode',
              message: null,
              source: 'message-node-' + i,
              target: elements[i]['data'].id,
              parent: null,
              bias: null,
            },
          })
      }
    }
  }

  private toggleNode(instance: cytoscape.Core, evt: any) {
    if (evt.target.selected()) {
      instance.nodes().unselect()
      this.unselectNode(evt.target)
    } else {
      this.unselectAllNodes()
      this.selectNode(evt.target)
    }
  }

  private unselectAllNodes() {
    cytoService.unselectAllNodes(window.cyMessage1)
    cytoService.unselectAllNodes(window.cyMessage2)
    this.selected = null
  }

  private selectNode(node: any) {
    if (node.data('type') !== 'group') {
      node.css('border-width', '1')
      // !!! Cannot do this.selected = node. This would end in an infinite loop.
      this.selected = this.newNode(node)
      if (node.data('type') === 'node') {
        node.toggleClass('deleteNode', true)
        this.sendEditNodeEvent(this.selected)
      }
    }
  }

  private sendEditNodeEvent(node: Node) {
    if (this.project) {
      const evt = new EditNodeEvent(this.project, node)
      this.$bus.$emit('node:edit', evt)
    }
  }

  private newNode(node: any) {
    const type = node.data('type')
    const className = node.data('className')
    const dbId = node.data('dbId')
    return new Node(type, className, dbId)
  }

  private unselectNode(node: any) {
    node.css('border-width', '1')
    node.toggleClass('delete', false)
    this.selected = null
    node.unselect()
    this.$bus.$emit('node:unselect')
  }

  private sendCreateNodeEvent(node: Node, className: NodeClassName) {
    if (this.project) {
      const evt = new CreateNodeEvent(this.project, node, className)
      this.$bus.$emit('node:create', evt)
    }
  }

  private clickAdd() {
    if (this.project) {
      this.sendCreateNodeEvent(
        new Node('root', 'Project', this.project.id),
        'TopicGroup'
      )
    }
  }

  private clickZoomIn() {
    cytoService.zoomIn(window.cyMessage1)
    cytoService.zoomIn(window.cyMessage2)
  }

  private clickZoomOut() {
    cytoService.zoomOut(window.cyMessage1)
    cytoService.zoomOut(window.cyMessage2)
  }

  private clickResetZoom() {
    window.cyMessage1.fit(undefined, this.PADDING)
    window.cyMessage2.fit(undefined, this.PADDING)
  }

  private activated() {
    bus.$on('reload-spider', async () => {
      await this.reloadAsync()
    })
  }

  private deactivated() {
    this.$bus.$off('reload-spider')
  }
}
